/**
 * useSecurity Hooks
 *
 * React Query hooks for the Security Monitoring Dashboard.
 * 30-second auto-refresh (same pattern as useSites).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSecurityDashboard,
  fetchSiteDashboard,
  fetchTunnels,
  fetchTunnelMetrics,
  fetchTrafficOverview,
  fetchSiteTraffic,
  fetchConnectivity,
  fetchSecurityAlerts,
  fetchAlertStatistics,
  acknowledgeAlert,
  resolveAlert,
  fetchSessions,
  terminateSession,
  fetchLoginAttempts,
  fetchLockouts,
  unlockAccount,
  fetchAuditLog,
  fetchMfaStatus,
  fetchAlertRules,
  updateAlertRule,
} from '../services/securityApi';
import type { TimeRange } from '../types/security.types';

const REFETCH_INTERVAL = 30_000;
const STALE_TIME = 25_000;

export function useSecurityDashboard() {
  return useQuery({
    queryKey: ['security', 'dashboard'],
    queryFn: fetchSecurityDashboard,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useTunnels() {
  return useQuery({
    queryKey: ['security', 'tunnels'],
    queryFn: fetchTunnels,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useTunnelMetrics(siteId: string, timeRange: TimeRange = '1h') {
  return useQuery({
    queryKey: ['security', 'tunnelMetrics', siteId, timeRange],
    queryFn: () => fetchTunnelMetrics(siteId, timeRange),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId,
  });
}

export function useTrafficOverview() {
  return useQuery({
    queryKey: ['security', 'traffic'],
    queryFn: fetchTrafficOverview,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useSiteTraffic(siteId: string, timeRange: TimeRange = '1h') {
  return useQuery({
    queryKey: ['security', 'siteTraffic', siteId, timeRange],
    queryFn: () => fetchSiteTraffic(siteId, timeRange),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId,
  });
}

export function useConnectivity() {
  return useQuery({
    queryKey: ['security', 'connectivity'],
    queryFn: fetchConnectivity,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useSecurityAlerts(filters?: {
  severity?: string;
  category?: string;
  site_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['security', 'alerts', filters],
    queryFn: () => fetchSecurityAlerts(filters),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useAlertStatistics() {
  return useQuery({
    queryKey: ['security', 'alertStatistics'],
    queryFn: fetchAlertStatistics,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, notes }: { alertId: string; notes?: string }) =>
      acknowledgeAlert(alertId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security'] });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => resolveAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security'] });
    },
  });
}

// ── Phase B: Auth Monitoring Hooks ─────────────────────────────────────────

export function useSessions(params?: { show_all?: boolean; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['security', 'sessions', params],
    queryFn: () => fetchSessions(params),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useTerminateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason?: string }) =>
      terminateSession(sessionId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: ['security', 'dashboard'] });
    },
  });
}

export function useLoginAttempts(params?: {
  email?: string;
  result?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['security', 'loginAttempts', params],
    queryFn: () => fetchLoginAttempts(params),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useLockouts(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['security', 'lockouts', params],
    queryFn: () => fetchLockouts(params),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

export function useUnlockAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lockoutId: string) => unlockAccount(lockoutId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security', 'lockouts'] });
      queryClient.invalidateQueries({ queryKey: ['security', 'dashboard'] });
    },
  });
}

export function useAuditLog(params?: {
  actor?: string;
  action?: string;
  target_type?: string;
  start?: string;
  end?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['security', 'auditLog', params],
    queryFn: () => fetchAuditLog(params),
    staleTime: STALE_TIME,
  });
}

export function useMfaStatus() {
  return useQuery({
    queryKey: ['security', 'mfaStatus'],
    queryFn: fetchMfaStatus,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
  });
}

// ── Phase C: Alert Rules, Site Dashboard ─────────────────────────────────────

export function useAlertRules() {
  return useQuery({
    queryKey: ['security', 'alertRules'],
    queryFn: fetchAlertRules,
    staleTime: STALE_TIME,
  });
}

export function useUpdateAlertRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, updates }: {
      ruleId: string;
      updates: { severity?: string; condition_config?: Record<string, unknown>; is_enabled?: boolean };
    }) => updateAlertRule(ruleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security', 'alertRules'] });
    },
  });
}

export function useSiteDashboard(siteId: string) {
  return useQuery({
    queryKey: ['security', 'siteDashboard', siteId],
    queryFn: () => fetchSiteDashboard(siteId),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId,
  });
}
